import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueries } from "@tanstack/react-query";
import { ArrowLeft, SlidersHorizontal, Star, BedDouble, SearchX, MapPin, Users, Calendar, Search, LayoutGrid, Map as MapIcon, RotateCcw } from "lucide-react";
import { format, parseISO, differenceInCalendarDays, addDays, subDays } from "date-fns";
import { PropertyCard } from "@/components/stays/PropertyCard";
import { MobileBottomNav } from "@/components/stays/MobileBottomNav";
import { PriceMarkerMap } from "@/components/stays/search/PriceMarkerMap";
import { SplitMap } from "@/components/stays/search/SplitMap";
import { StayHeader } from "@/components/stays/StayHeader";
import { ResultsDrawer } from "@/components/stays/search/ResultsDrawer";
import { SearchOverlay } from "@/components/stays/search/SearchOverlay";
import { PriceFilter } from "@/components/stays/PriceFilter";
import { getProperties, getMapPoints, getRooms, getAvailability, getReviews, distanceKm, getPlaceDetails, suggestPlaces } from "@/lib/stayflowApi";
import { distanceLabel as fmtDistance, isNear, newPlaceSession, isKazirangaPick, KOHORA_PLACE_ID, KAZIRANGA_LABEL } from "@/lib/places";
import { CATEGORIES, matchesCategory } from "@/lib/stayCategories";
import { getDemoStays, seedDemoStays, deleteDemoStay, clearDemoStays } from "@/lib/demoStore";
import { setMeta, SITE_URL } from "@/lib/seo";

const iso = (d) => format(d, "yyyy-MM-dd");

const StaysMapSearch = () => {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  useEffect(() => {
    setMeta({ title: "Search stays — StayFlow", description: "Explore stays on the map.", url: `${SITE_URL}/stays/search` });
  }, []);

  // ---- URL params ----
  const location = sp.get("location") || "";
  const placeId = sp.get("placeId") || "";
  const placeName = sp.get("place") || "";
  const near = sp.get("near");
  const userLoc = useMemo(() => {
    if (!near) return null;
    const [lat, lng] = near.split(",").map(Number);
    return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
  }, [near]);
  const checkIn = sp.get("checkIn"), checkOut = sp.get("checkOut");
  const flex = Math.min(Number(sp.get("flex")) || 0, 3);
  const adults = Number(sp.get("adults")) || 2;
  const children = Number(sp.get("children")) || 0;
  const rooms = Number(sp.get("rooms")) || 1;
  const guests = adults + children;

  // ---- data ----
  const propsQ = useQuery({ queryKey: ["properties"], queryFn: getProperties, staleTime: 5 * 60 * 1000, retry: 2 });
  const mapQ = useQuery({ queryKey: ["map-points"], queryFn: getMapPoints, staleTime: 10 * 60 * 1000, retry: 2 });
  const realProps = useMemo(() => (Array.isArray(propsQ.data) ? propsQ.data : []), [propsQ.data]);

  // ---- deletable demo stays (localStorage; per-browser, never touches upstream) ----
  const [demoStays, setDemoStays] = useState(() => getDemoStays());
  useEffect(() => {
    const on = () => setDemoStays(getDemoStays());
    window.addEventListener("sf-demo", on);
    return () => window.removeEventListener("sf-demo", on);
  }, []);

  const properties = useMemo(() => [...demoStays, ...realProps], [demoStays, realProps]);
  const geoById = useMemo(() => {
    const m = Object.fromEntries((mapQ.data || []).map((g) => [g.id, g]));
    demoStays.forEach((d) => { m[d.id] = { id: d.id, lat: d.lat, lng: d.lng, city: d.city, state: d.state }; });
    return m;
  }, [mapQ.data, demoStays]);

  const ratingQs = useQueries({
    queries: properties.map((p) => ({ queryKey: ["reviews", p.id], queryFn: () => getReviews(p.id), staleTime: 60 * 60 * 1000, retry: 1, enabled: !p.__demo })),
  });
  const ratingById = useMemo(() => Object.fromEntries(properties.map((p, i) => [p.id, p.__demo ? Number(p.rating || 0) : Number(ratingQs[i]?.data?.rating || 0)])), [properties, ratingQs]);

  const roomsQs = useQueries({
    queries: properties.map((p) => ({ queryKey: ["rooms", p.id], queryFn: () => getRooms(p.id), staleTime: 5 * 60 * 1000, retry: 1, enabled: !p.__demo })),
  });
  const bedsById = useMemo(() => Object.fromEntries(properties.map((p, i) => {
    if (p.__demo) return [p.id, Number(p.beds || 0)];
    const rs = roomsQs[i]?.data || [];
    return [p.id, Math.max(0, ...rs.map((r) => Number(r.beds) || 0))];
  })), [properties, roomsQs]);

  // ---- the place the guest picked (or an old ?location= link looked up as a place) ----
  const placeQ = useQuery({
    queryKey: ["place-details", placeId],
    queryFn: () => getPlaceDetails(placeId, newPlaceSession()),
    enabled: !!placeId,
    staleTime: 60 * 60 * 1000,
  });

  const nameMatchesStay = useMemo(() => {
    const q = location.trim().toLowerCase();
    if (!q) return false;
    return properties.some((p) => (p.name || "").toLowerCase().includes(q));
  }, [location, properties]);

  const [legacyPlace, setLegacyPlace] = useState(null);
  useEffect(() => {
    // Old shared links carry only a word. Stay names win; otherwise look the word
    // up as a place, and fall back to word search when Google has nothing.
    if (placeId || !location.trim() || nameMatchesStay) { setLegacyPlace(null); return; }
    let dead = false;
    (async () => {
      try {
        const session = newPlaceSession();
        const r = await suggestPlaces(location.trim(), session);
        const top = (r.places || [])[0];
        if (!top || dead) return;
        const d = await getPlaceDetails(top.place_id, session);
        if (!dead && d) setLegacyPlace(d);
      } catch { /* word search stays */ }
    })();
    return () => { dead = true; };
  }, [placeId, location, nameMatchesStay]);

  const picked = placeQ.data || legacyPlace;
  const kaziranga = isKazirangaPick(picked?.name, placeName, location);

  const gateQ = useQuery({
    queryKey: ["place-details", KOHORA_PLACE_ID],
    queryFn: () => getPlaceDetails(KOHORA_PLACE_ID, newPlaceSession()),
    enabled: kaziranga,
    staleTime: 60 * 60 * 1000,
  });

  const focus = useMemo(() => {
    if (!picked?.lat) return null;
    const gate = kaziranga ? gateQ.data : null;
    return {
      ...picked,
      name: kaziranga ? KAZIRANGA_LABEL : (picked.name || placeName || location),
      lat: gate?.lat ?? picked.lat,
      lng: gate?.lng ?? picked.lng,
      pinLat: picked.lat,
      pinLng: picked.lng,
    };
  }, [picked, kaziranga, gateQ.data, placeName, location]);

  const focusPoint = focus ? { lat: focus.pinLat, lng: focus.pinLng, label: focus.name } : null;
  const originPoint = focus ? { lat: focus.lat, lng: focus.lng } : null;

  // ---- filter pill state ----
  const priceBounds = useMemo(() => {
    const prices = properties.map((p) => p.min_price_per_night).filter((n) => typeof n === "number");
    if (prices.length === 0) return [0, 0];
    let lo = Math.floor(Math.min(...prices) / 100) * 100;
    const hi = Math.ceil(Math.max(...prices) / 100) * 100;
    if (lo >= hi) lo = Math.max(0, hi - 500);
    return [lo, hi];
  }, [properties]);
  const [priceRange, setPriceRange] = useState(priceBounds);
  useEffect(() => { setPriceRange(priceBounds); }, [priceBounds]);
  const [cats, setCats] = useState(() => new Set());
  const [topRated, setTopRated] = useState(false);
  const [twoBeds, setTwoBeds] = useState(false);

  // ---- desktop split-screen state ----
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  const [showMap, setShowMap] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [searchAsMove, setSearchAsMove] = useState(false);
  const [bounds, setBounds] = useState(null);
  const resetFilters = () => { setCats(new Set()); setTopRated(false); setTwoBeds(false); setPriceRange(priceBounds); };

  const toggleCat = (id) => setCats((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ---- base filtering ----
  const baseFiltered = useMemo(() => {
    const q = focus ? "" : location.trim().toLowerCase();
    return properties.filter((p) => {
      const g = geoById[p.id] || {};
      const hay = [p.name, p.city, p.state, p.address, p.type, p.property_type, g.city, g.state, g.address].filter(Boolean).join(" ").toLowerCase();
      const catHay = [p.name, p.type, p.property_type].filter(Boolean).join(" ");
      if (q && !hay.includes(q)) return false;
      if (cats.size > 0 && ![...cats].some((c) => matchesCategory(catHay, c))) return false;
      if (typeof p.min_price_per_night === "number" && (p.min_price_per_night < priceRange[0] || p.min_price_per_night > priceRange[1])) return false;
      if (topRated && !(ratingById[p.id] >= 4.5)) return false;
      if (twoBeds && !(bedsById[p.id] >= 2)) return false;
      return true;
    });
  }, [properties, geoById, location, focus, cats, priceRange, topRated, twoBeds, ratingById, bedsById]);

  // ---- availability (± flex days widen the window) ----
  const [availableIds, setAvailableIds] = useState(null);
  const [checking, setChecking] = useState(false);
  const baseRef = useRef(baseFiltered);
  baseRef.current = baseFiltered;
  const baseKey = baseFiltered.map((p) => p.id).join(",");

  useEffect(() => {
    if (!checkIn || !checkOut) { setAvailableIds(null); return; }
    let cancelled = false;
    const from = parseISO(checkIn), to = parseISO(checkOut);
    const nights = Math.max(differenceInCalendarDays(to, from), 1);
    setChecking(true);
    (async () => {
      const results = await Promise.all(baseRef.current.filter((p) => !p.__demo).map(async (p) => {
        try {
          const rs = await getRooms(p.id);
          const checks = await Promise.all((rs || []).map(async (r) => {
            try {
              const a = await getAvailability(r.id, iso(subDays(from, flex)), iso(addDays(to, flex)));
              const busy = new Set(a?.busy_days || []);
              for (let s = -flex; s <= flex; s++) {
                const start = addDays(from, s);
                if (start < new Date(new Date().setHours(0, 0, 0, 0))) continue;
                const wanted = Array.from({ length: nights }, (_, i) => iso(addDays(start, i)));
                if (!wanted.some((n) => busy.has(n))) return true;
              }
              return false;
            } catch { return true; }
          }));
          return { id: p.id, ok: checks.length === 0 || checks.some(Boolean) };
        } catch { return { id: p.id, ok: true }; }
      }));
      if (cancelled) return;
      setAvailableIds(new Set(results.filter((r) => r.ok).map((r) => r.id)));
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [checkIn, checkOut, flex, baseKey]);

  const origin = userLoc || originPoint;
  const kmFor = (id) => distanceKm(origin, geoById[id]);

  const finalList = useMemo(() => {
    let list = (!checkIn || availableIds === null) ? baseFiltered : baseFiltered.filter((p) => p.__demo || availableIds.has(p.id));
    if (origin) list = [...list].sort((a, b) => distanceKm(origin, geoById[a.id]) - distanceKm(origin, geoById[b.id]));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFiltered, availableIds, checkIn, origin?.lat, origin?.lng, geoById]);

  // Nearest first, then "Further away", then stays with no location at all.
  const makeSections = (list) => {
    if (!origin) return [{ key: "all", title: null, items: list }];
    const near = [], far = [], noLoc = [];
    list.forEach((p) => {
      const g = geoById[p.id];
      if (!g || g.lat == null) { noLoc.push(p); return; }
      (isNear(focus, g, distanceKm(origin, g)) ? near : far).push(p);
    });
    const where = focus?.name || null;
    const out = [];
    if (near.length) out.push({ key: "near", title: where ? `Stays near ${where}` : "Stays near you", items: near });
    if (far.length) out.push({ key: "far", title: near.length ? "Further away" : (where ? `No stays near ${where} yet. Here are the nearest.` : "No stays near you yet. Here are the nearest."), items: far });
    if (noLoc.length) out.push({ key: "noloc", title: "Location not shared yet", items: noLoc });
    return out;
  };

  const labelFor = (id) => {
    if (!origin) return null;
    const g = geoById[id];
    if (!g || g.lat == null) return null;
    // A state or district pick has no meaningful centre to measure from, and the
    // card already prints the stay's town + state — so no distance line at all.
    if (focus?.kind === "state" || focus?.kind === "district") return null;
    return fmtDistance(distanceKm(origin, g), focus?.name || null);
  };

  const mapPoints = useMemo(() => finalList.map((p) => ({
    ...(geoById[p.id] || {}), id: p.id, name: p.name, min_price_per_night: p.min_price_per_night, min_price_incl_gst: p.min_price_incl_gst,
  })), [finalList, geoById]);

  const nights = checkIn && checkOut ? differenceInCalendarDays(parseISO(checkOut), parseISO(checkIn)) : 0;
  const cardQuery = (() => {
    const p = new URLSearchParams();
    if (checkIn && checkOut) { p.set("checkIn", checkIn); p.set("checkOut", checkOut); }
    p.set("guests", String(guests)); p.set("rooms", String(rooms)); p.set("adults", String(adults)); p.set("children", String(children));
    return `?${p.toString()}`;
  })();

  // ---- desktop: "search as I move" filters the list/markers to the map viewport ----
  const inBounds = (g) => !bounds || (g?.lat != null && g.lat <= bounds.north && g.lat >= bounds.south && g.lng <= bounds.east && g.lng >= bounds.west);
  const displayList = useMemo(
    () => (searchAsMove && bounds ? finalList.filter((p) => inBounds(geoById[p.id])) : finalList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalList, searchAsMove, bounds, geoById]
  );
  const displayPoints = useMemo(() => displayList.map((p) => ({
    ...(geoById[p.id] || {}), id: p.id, name: p.name, min_price_per_night: p.min_price_per_night, min_price_incl_gst: p.min_price_incl_gst,
  })), [displayList, geoById]);
  const metaById = useMemo(() => Object.fromEntries(finalList.map((p) => [p.id, {
    title: p.name, image: p.cover_image, rating: Number(ratingById[p.id] || 0), price: p.min_price_incl_gst ?? p.min_price_per_night, demo: !!p.__demo,
  }])), [finalList, ratingById]);

  // ---- marker selection → drawer scroll/highlight ----
  const [selectedId, setSelectedId] = useState(null);
  const [snapSignal, setSnapSignal] = useState(null);
  const cardRefs = useRef({});
  const onMarkerSelect = (id) => {
    setSelectedId(id);
    setSnapSignal({ snap: "half", ts: Date.now() });
    setTimeout(() => cardRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" }), 320);
  };

  // ---- edit search overlay ----
  const [editOpen, setEditOpen] = useState(false);
  const overlayInitial = useMemo(() => ({
    search: placeName || location,
    picked: placeId ? { place_id: placeId, main: placeName || location } : null,
    near: userLoc,
    dateRange: checkIn && checkOut ? { from: parseISO(checkIn), to: parseISO(checkOut) } : undefined,
    flex, adults, children, rooms,
    infants: Number(sp.get("infants")) || 0,
    pets: Number(sp.get("pets")) || 0,
  }), [location, placeId, placeName, userLoc, checkIn, checkOut, flex, adults, children, rooms, sp]);

  const headTitle = userLoc ? "Homes nearby" : (focus?.name || placeName || location) ? `Homes in ${focus?.name || placeName || location}` : "All homes";
  const headSub = [
    checkIn && checkOut ? `${format(parseISO(checkIn), "d")}–${format(parseISO(checkOut), "d MMM")}` : null,
    `${guests} guest${guests !== 1 ? "s" : ""}`,
  ].filter(Boolean).join(" · ");

  const dateLabel = checkIn && checkOut ? `${format(parseISO(checkIn), "d MMM")} – ${format(parseISO(checkOut), "d MMM")}` : "Any week";
  const onViewStay = (id) => navigate(`/stays/${id}${cardQuery}`);
  const whereLabel = focus?.name || placeName || location;
  const resultHeading = checking ? "Finding stays…" : `${displayList.length} ${displayList.length === 1 ? "stay" : "stays"}${whereLabel ? ` in ${whereLabel}` : userLoc ? " nearby" : ""}`;

  // ================= DESKTOP SPLIT-SCREEN (≥1024px) =================
  if (isDesktop) {
    return (
      <div className="fixed inset-0 flex flex-col bg-paper animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="map-search-desktop">
        {/* Top search summary bar */}
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-black/10 px-8 py-3.5 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/")} data-testid="split-back" className="shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-ink-soft hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> StayFlow
          </button>
          <button onClick={() => setEditOpen(true)} data-testid="split-search-pill"
            className="flex items-center divide-x divide-black/10 rounded-full border border-black/15 shadow-sm hover:shadow-md transition-shadow bg-white overflow-hidden">
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-ink"><MapPin className="w-4 h-4 text-brand" /> {whereLabel || (userLoc ? "Nearby" : "Anywhere")}</span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-ink-soft"><Calendar className="w-4 h-4" /> {dateLabel}</span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-ink-soft"><Users className="w-4 h-4" /> {guests} guest{guests !== 1 ? "s" : ""}</span>
            <span className="px-1.5"><span className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center"><Search className="w-4 h-4" /></span></span>
          </button>
          <div className="shrink-0 flex items-center gap-4">
            <a href="/home" className="text-sm font-semibold text-ink-soft hover:text-ink transition-colors">List your property</a>
            <button onClick={() => navigate("/profile")} data-testid="split-profile" className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center text-sm font-bold">
              <Users className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Secondary filter bar */}
        <div className="px-8 py-3 border-b border-black/10 bg-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <PriceFilter priceRange={priceRange} setPriceRange={setPriceRange} priceBounds={priceBounds} />
            <button onClick={() => setTopRated((v) => !v)} data-testid="split-pill-toprated" aria-pressed={topRated}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold border transition-colors active:scale-95 ${topRated ? "bg-ink text-white border-ink" : "bg-white text-ink-soft border-black/10"}`}>
              <Star className={`w-3.5 h-3.5 ${topRated ? "fill-white text-white" : "fill-ink text-ink"}`} /> Top rated 4.5+
            </button>
            {CATEGORIES.filter((c) => c.id !== "all").map((c) => {
              const Icon = c.icon; const active = cats.has(c.id);
              return (
                <button key={c.id} onClick={() => toggleCat(c.id)} data-testid={`split-pill-${c.id}`} aria-pressed={active}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold border transition-colors active:scale-95 ${active ? "bg-ink text-white border-ink" : "bg-white text-ink-soft border-black/10"}`}>
                  <Icon className={`w-4 h-4 ${active ? "text-white" : "text-brand"}`} /> {c.label}
                </button>
              );
            })}
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {demoStays.length > 0 ? (
              <button onClick={clearDemoStays} data-testid="split-clear-demo"
                className="inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold border border-brand/30 text-brand hover:bg-brand-soft transition-colors active:scale-95">
                Clear demo ({demoStays.length})
              </button>
            ) : (
              <button onClick={() => seedDemoStays(30)} data-testid="split-seed-demo"
                className="inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold border border-black/10 text-ink-soft hover:bg-paper transition-colors active:scale-95">
                Seed 30 demo stays
              </button>
            )}
            <button onClick={() => setShowMap((v) => !v)} data-testid="split-toggle-map"
              className="inline-flex items-center gap-2 rounded-full h-10 px-5 text-[13px] font-bold bg-ink text-white active:scale-95 transition">
              {showMap ? <><LayoutGrid className="w-4 h-4" /> Show grid</> : <><MapIcon className="w-4 h-4" /> Show map</>}
            </button>
          </div>
        </div>

        {/* Split viewport */}
        <div className="flex-1 flex overflow-hidden">
          <div className={`${showMap ? "w-[55%]" : "w-full"} h-full overflow-y-auto overscroll-contain px-8 py-6`} data-testid="split-list" data-lenis-prevent>
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-xl font-heading font-extrabold text-ink" data-testid="split-count">{resultHeading}</h1>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-medium mb-5">
              🏷️ Upfront pricing · all taxes &amp; local fees included
            </span>

            {propsQ.isLoading && <p className="text-center text-ink-soft py-16">Finding stays…</p>}

            {!propsQ.isLoading && displayList.length === 0 && (
              <div className="rounded-3xl border border-black/10 bg-white p-10 text-center mt-4" data-testid="split-empty">
                <SearchX className="w-9 h-9 mx-auto text-ink-faint" />
                <p className="mt-3 font-heading font-bold text-lg text-ink">No stays found matching these filters{whereLabel ? ` in ${whereLabel}` : ""}.</p>
                <p className="mt-1 text-sm text-ink-soft">Try adjusting your filters or search area.</p>
                <div className="mt-4 flex items-center justify-center gap-2.5">
                  <button onClick={resetFilters} data-testid="split-reset" className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold active:scale-95 transition"><RotateCcw className="w-4 h-4" /> Reset all filters</button>
                  <button onClick={() => setEditOpen(true)} data-testid="split-edit-search" className="rounded-full border border-black/15 px-5 py-2.5 text-sm font-semibold text-ink hover:bg-paper transition-colors">Edit search</button>
                </div>
              </div>
            )}

            {makeSections(displayList).map((sec) => (
              <div key={sec.key} className="mb-7">
                {sec.title && <h2 className="mb-3 text-base font-heading font-bold text-ink" data-testid={`split-section-${sec.key}`}>{sec.title}</h2>}
                <div className={`grid gap-5 ${showMap ? "grid-cols-2" : "grid-cols-3"} ${checking ? "opacity-60" : ""}`}>
                  {sec.items.map((p, i) => (
                    <div key={p.id} ref={(el) => { cardRefs.current[p.id] = el; }}
                      onMouseEnter={() => setHoveredId(p.id)} onMouseLeave={() => setHoveredId(null)}
                      className={`scroll-mt-4 rounded-3xl transition-shadow ${selectedId === p.id ? "ring-2 ring-brand ring-offset-2" : ""}`}
                      data-testid={`split-result-${p.id}`}>
                      <PropertyCard property={p} index={i} query={cardQuery} locationOverride={geoById[p.id]} nights={nights} distanceLabel={labelFor(p.id)} onDelete={p.__demo ? deleteDemoStay : undefined} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {showMap && (
            <div className="w-[45%] h-full border-l border-black/10 relative" data-testid="split-map-pane">
              <SplitMap
                points={displayPoints} selectedId={selectedId} hoveredId={hoveredId}
                onSelect={(id) => (id ? onMarkerSelect(id) : setSelectedId(null))} onHover={setHoveredId}
                userLoc={userLoc} focus={focusPoint} metaById={metaById}
                searchAsMove={searchAsMove} setSearchAsMove={setSearchAsMove}
                onBoundsChange={setBounds} onViewStay={onViewStay}
              />
            </div>
          )}
        </div>

        <SearchOverlay open={editOpen} onClose={() => setEditOpen(false)} initial={overlayInitial} stays={properties} onPickStay={(st) => navigate(`/stays/${st.id}${cardQuery}`)} onSearch={(p) => setSp(p)} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-paper overflow-hidden" data-testid="map-search-page">
      <PriceMarkerMap points={mapPoints} selectedId={selectedId} onSelect={onMarkerSelect} userLoc={userLoc} focus={focusPoint}
        metaById={metaById} onViewStay={onViewStay} onClose={() => setSelectedId(null)} />

      {/* ---- Floating top header ---- */}
      <div className="absolute top-0 inset-x-0 z-30 pt-3">
        <div className="px-3 flex items-center gap-2">
          <button onClick={() => navigate("/stays")} aria-label="Back to explore" data-testid="map-back-btn"
            className="shrink-0 w-11 h-11 rounded-full bg-white shadow-md border border-black/5 flex items-center justify-center active:scale-95 transition-transform">
            <ArrowLeft className="w-5 h-5 text-ink" />
          </button>
          <button onClick={() => setEditOpen(true)} data-testid="map-summary-pill"
            className="flex-1 min-w-0 min-h-[48px] rounded-full bg-white shadow-md border border-black/5 px-5 py-1.5 text-center active:scale-[0.98] transition-transform">
            <span className="block font-heading font-bold text-sm text-ink truncate leading-tight">{headTitle}</span>
            <span className="block text-xs text-ink-faint truncate">{headSub}</span>
          </button>
          <button onClick={() => setEditOpen(true)} aria-label="Edit filters" data-testid="map-filter-btn"
            className="shrink-0 w-11 h-11 rounded-full bg-white shadow-md border border-black/5 flex items-center justify-center active:scale-95 transition-transform">
            <SlidersHorizontal className="w-[18px] h-[18px] text-ink" />
          </button>
        </div>

        {/* ---- Data-driven filter pills ---- */}
        <div className="mt-2.5 flex items-center gap-2 overflow-x-auto no-scrollbar px-3 pb-1" data-testid="map-filter-pills">
          <span className="shrink-0 shadow-md rounded-full"><PriceFilter priceRange={priceRange} setPriceRange={setPriceRange} priceBounds={priceBounds} /></span>
          <button onClick={() => setTopRated((v) => !v)} data-testid="map-pill-toprated" aria-pressed={topRated}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold shadow-md transition-colors active:scale-95 ${topRated ? "bg-ink text-white" : "bg-white text-ink-soft border border-black/5"}`}>
            <Star className={`w-3.5 h-3.5 ${topRated ? "fill-white text-white" : "fill-ink text-ink"}`} /> 4.5+
          </button>
          {Object.values(bedsById).some((b) => b >= 2) && (
          <button onClick={() => setTwoBeds((v) => !v)} data-testid="map-pill-beds" aria-pressed={twoBeds}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold shadow-md transition-colors active:scale-95 ${twoBeds ? "bg-ink text-white" : "bg-white text-ink-soft border border-black/5"}`}>
            <BedDouble className="w-4 h-4" /> 2+ beds
          </button>
          )}
          {CATEGORIES.filter((c) => c.id !== "all").map((c) => {
            const Icon = c.icon;
            const active = cats.has(c.id);
            return (
              <button key={c.id} onClick={() => toggleCat(c.id)} data-testid={`map-pill-${c.id}`} aria-pressed={active}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-[13px] font-semibold shadow-md transition-colors active:scale-95 ${active ? "bg-ink text-white" : "bg-white text-ink-soft border border-black/5"}`}>
                <Icon className={`w-4 h-4 ${active ? "text-white" : "text-brand"}`} /> {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Draggable listing drawer ---- */}
      <ResultsDrawer snapSignal={snapSignal} resultLabel={checking ? "Checking…" : `${finalList.length} stay${finalList.length !== 1 ? "s" : ""}`}>
        {propsQ.isLoading && <p className="text-center text-ink-soft py-10" data-testid="map-results-loading">Finding stays…</p>}
        {!propsQ.isLoading && finalList.length === 0 && (
          <div className="text-center py-10" data-testid="map-results-empty">
            <SearchX className="w-9 h-9 mx-auto text-ink-faint" aria-hidden="true" />
            <p className="mt-3 font-heading font-bold text-lg text-ink">No stays match this search</p>
            <p className="mt-1 text-sm text-ink-soft">Try widening your dates, area or filters.</p>
          </div>
        )}
        <div className={`transition-opacity ${checking ? "opacity-60" : ""}`}>
          {makeSections(finalList).map((sec) => (
            <div key={sec.key} className="mb-7">
              {sec.title && <h2 className="mb-3 font-heading font-bold text-ink" data-testid={`map-section-${sec.key}`}>{sec.title}</h2>}
              <div className="space-y-7">
                {sec.items.map((p, i) => (
                  <div key={p.id} ref={(el) => { cardRefs.current[p.id] = el; }} className={`scroll-mt-4 rounded-2xl transition-shadow ${selectedId === p.id ? "ring-2 ring-brand ring-offset-2" : ""}`} data-testid={`map-result-${p.id}`}>
                    <PropertyCard property={p} index={i} query={cardQuery} locationOverride={geoById[p.id]} nights={nights} distanceLabel={labelFor(p.id)} onDelete={p.__demo ? deleteDemoStay : undefined} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ResultsDrawer>

      <MobileBottomNav />

      <SearchOverlay open={editOpen} onClose={() => setEditOpen(false)} initial={overlayInitial} stays={properties} onPickStay={(st) => navigate(`/stays/${st.id}${cardQuery}`)} onSearch={(p) => setSp(p)} />
    </div>
  );
};

export default StaysMapSearch;
